import { ApprovalsPersons } from "@/components/approvals/ApprovalsPersons.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useApprovalRequestApprove,
  useApprovalRequestCancel,
  useApprovalRequestDetails,
  useApprovalRequestReject,
} from "@/queries/approval/use-approval-requests.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiApprovalRequestStatus,
  ApiSurveyWriteToFieldChange,
} from "@/types/approvals/approval.types.ts";
import { ApiFieldTypes, ApiFieldValueTypes } from "@/types/custom-field.types.ts";
import { EmployeeGenders } from "@/types/employees/employee.types.ts";
import { ApiPermission, PermissionType } from "@/types/role.types.ts";
import {
  Button,
  ButtonLink,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  ListItem,
  ListOption,
  Modal,
  ModalProps,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CompensationLinesValue } from "@/components/questionnaire/components/CompensationLinesValue.tsx";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";

interface WorkflowRequestViewModalProps extends ModalProps {
  requestId?: number;
}

export function WorkflowRequestViewModal({ requestId, ...props }: WorkflowRequestViewModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close, openModal } = useModals();
  const { showInfo } = useToasts();
  const { renderEmployeeName, renderDate, renderDateAndTime, renderTimeAgo } = useFormatter();
  const { getFieldTitle, getTableTitle, renderFieldDataInCell } = useCustomFieldsHook();
  const { data: requestDetails, isLoading } = useApprovalRequestDetails(requestId);
  const changes = requestDetails?.workflow_data?.changes ?? [];
  const { data: terminationLeaveTypes } = useOrganizationListByType(
    ApiOrganizationListType.termination_leave_types,
    changes.some((change) => change.field.type === ApiFieldTypes.employee_termination_leave_type)
  );
  const { data: terminationReasons } = useOrganizationListByType(
    ApiOrganizationListType.termination_reasons,
    changes.some((change) => change.field.type === ApiFieldTypes.employee_termination_reason)
  );
  const approveRequest = useApprovalRequestApprove();
  const rejectRequest = useApprovalRequestReject();
  const cancelRequest = useApprovalRequestCancel();
  const { canIEdit, isFeatureEnabled } = usePermissions();

  const [isApproveButtonShouldLoading, setIsApprove] = useState(false);
  const [useComment, setUseComment] = useState(false);
  const [comment, setComment] = useState("");

  useEffectOnce(() => {
    if (!requestId) return;

    analyticsService.trackEvent(analyticEvents.approvals.requestApprovalViewed, {
      [analyticProperties.id]: requestId,
      [analyticProperties.title]: "Workflow",
      [analyticProperties.actionType]: "View",
    });
  });

  const handleAnswerRequest = async (approve: boolean, isBypassApprovalFlow: boolean = false) => {
    if (!requestDetails?.approval_request_details) return;

    setIsApprove(approve);

    const requestId = requestDetails.approval_request_details.id;
    const payload = {
      id: requestId,
      payload: {
        is_bypass_approval_flow: Boolean(
          isBypassApprovalFlow && requestDetails.acl.can_bypass_flow
        ),
        comment: useComment ? comment : null,
      },
    };

    const trackAnalytics = () => {
      analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
        [analyticProperties.id]: requestId,
        [analyticProperties.title]: "Workflow",
        [analyticProperties.actionType]: approve ? "Approve" : "Reject",
      });

      showInfo({
        title: approve ? t("approval:request_approved") : t("approval:request_rejected"),
      });
    };

    const applyAnswer = async () => {
      if (approve) {
        await approveRequest.mutateAsync(payload);
      } else {
        await rejectRequest.mutateAsync(payload);
      }
      trackAnalytics();
    };

    if (requestDetails.acl.can_bypass_flow && isBypassApprovalFlow) {
      openModal(ConfirmationModal, {
        question: t(approve ? "approval:approve_as_admin" : "approval:reject_as_admin"),
        text: t(
          approve ? "approval:approve_as_admin_description" : "approval:reject_as_admin_description"
        ),
        acceptTitle: t(approve ? "approval:approve" : "approval:reject"),
        onAccept: async function () {
          try {
            await applyAnswer();
          } catch {
            return;
          }
        },
      });
    } else {
      try {
        await applyAnswer();
      } catch {
        return;
      }
    }
  };

  const handleCancelRequest = () => {
    if (!requestDetails?.approval_request_details?.id) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!requestDetails.approval_request_details) return;

          await cancelRequest.mutateAsync(requestDetails.approval_request_details.id);

          analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
            [analyticProperties.id]: requestDetails.approval_request_details.id,
            [analyticProperties.title]: "Workflow",
            [analyticProperties.actionType]: "Cancel",
          });

          showInfo({ title: t("approval:request_canceled") });
        } catch {
          return;
        }
      },
    });
  };

  const approvalRequest = requestDetails?.approval_request_details;
  const workflowData = requestDetails?.workflow_data;
  const canBypassFlow = requestDetails?.acl.can_bypass_flow;
  const shouldShowActionsMenu = Boolean(requestDetails?.acl.can_cancel || canBypassFlow);
  const isAnswerPending = approveRequest.isPending || rejectRequest.isPending;

  const employeeProfileChanges = changes.filter((change) => !change.table);
  const tableChanges = changes.reduce((groups, change) => {
    if (!change.table) return groups;

    const group = groups.get(change.table.id);
    if (group) {
      group.changes.push(change);
    } else {
      groups.set(change.table.id, {
        title: getTableTitle(change.table),
        changes: [change],
      });
    }

    return groups;
  }, new Map<number, { title: string; changes: ApiSurveyWriteToFieldChange[] }>());
  const changeGroups = [
    ...(employeeProfileChanges.length
      ? [
          {
            key: "employee-profile",
            title: t("core:settings_employee_profile_title"),
            changes: employeeProfileChanges,
          },
        ]
      : []),
    ...Array.from(tableChanges, ([tableId, group]) => ({
      key: `table-${tableId}`,
      ...group,
    })),
  ];

  const isChangedValueEmpty = (value: ApiSurveyWriteToFieldChange["value_before"]) =>
    !value ||
    Object.values(value).every(
      (item) => item == null || item === "" || (Array.isArray(item) && item.length === 0)
    );

  const hasValueBefore = (change: ApiSurveyWriteToFieldChange) =>
    !change.table ||
    (change.field.type === ApiFieldTypes.employee_compensation_lines
      ? !!change.compensation_lines_before?.length
      : !isChangedValueEmpty(change.value_before));

  const renderChangedValue = (
    change: ApiSurveyWriteToFieldChange,
    value: ApiSurveyWriteToFieldChange["value_before"]
  ) => {
    if (isChangedValueEmpty(value)) return "-";
    if (change.field.type === ApiFieldTypes.employee_gender) {
      const gender = EmployeeGenders.find((item) => item.id === value?.value_text);
      return gender ? t(gender.title) : "-";
    }
    if (change.field.field_type === ApiFieldValueTypes.datetime) {
      return value?.datetime_value ? renderDateAndTime(value?.datetime_value) : "-";
    }

    const organizationList =
      change.field.type === ApiFieldTypes.employee_termination_leave_type
        ? terminationLeaveTypes
        : change.field.type === ApiFieldTypes.employee_termination_reason
          ? terminationReasons
          : undefined;
    const field = {
      ...change.field,
      section_id: 0,
      table_id: change.table?.id ?? 0,
      is_archived: false,
      options:
        organizationList?.items.map((item) => ({
          id: item.id,
          title: item.title,
          description: "",
          is_archived: item.is_archived,
        })) ?? change.field.options,
    };
    return renderFieldDataInCell(field, [
      {
        field,
        permission_value: PermissionType.view,
        value,
      },
    ]);
  };

  return (
    <Modal
      title={isLoading ? t("common:loading") : t("approval:approval_request")}
      closeByOutsidePress={approvalRequest?.status !== ApiApprovalRequestStatus.awaiting}
      {...props}
      size={"lg"}
      headerActionsSlot={
        shouldShowActionsMenu ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            {requestDetails?.acl.can_cancel && (
              <ListOption
                title={t("approval:cancel_request") + "..."}
                onClick={() => handleCancelRequest()}
              />
            )}
            {requestDetails?.acl.can_cancel && canBypassFlow && <DropdownMenuSeparator />}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:approve_as_admin") + "..."}
                description={t("approval:approve_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => void handleAnswerRequest(true, true)}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:reject_as_admin") + "..."}
                description={t("approval:reject_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => void handleAnswerRequest(false, true)}
              />
            )}
          </DropdownMenu>
        ) : undefined
      }
      actions={
        requestDetails?.acl.can_reject || requestDetails?.acl.can_approve ? (
          <Stack gap={"smd"} className={"pt-1"}>
            <Stack gap={"sm"}>
              <Switch
                size={"sm"}
                checked={useComment}
                onChange={setUseComment}
                title={t("common:add_comment")}
              />
              {useComment && (
                <Textarea
                  label={t("approval:comment_your_decision") + "..."}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              )}
            </Stack>
            <Stack gap={"sm"} justify={"between"}>
              {requestDetails?.acl.can_approve && (
                <Button
                  type={"submit"}
                  size={"lg"}
                  className={"w-full"}
                  isLoading={isAnswerPending && isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(true);
                  }}
                >
                  {t("approval:approve")}
                </Button>
              )}
              {requestDetails.acl.can_reject && (
                <Button
                  variant={"plain"}
                  type={"submit"}
                  className={"w-full"}
                  isLoading={isAnswerPending && !isApproveButtonShouldLoading}
                  onClick={() => {
                    void handleAnswerRequest(false);
                  }}
                >
                  {t("approval:reject")}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <Button
            size={"lg"}
            className={"w-full"}
            variant={"secondary"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:close")}
          </Button>
        )
      }
      withCloser={true}
    >
      <>
        {isLoading && <Text className={"text-secondary"}>{t("common:loading")}</Text>}
        {approvalRequest && (
          <Stack>
            <Stack gap={"md"}>
              <Stack>
                <Title size={"xs"} header={t("approval:changes_for")} paddingBottom />
                <Stack direction={"horizontal"} gap={"md"}>
                  <EmployeeListItem
                    employee={approvalRequest.employee}
                    valueSlot={
                      <Button
                        onClick={() => {
                          openModal(EmployeeViewModal, {
                            employeeId: approvalRequest.employee.id,
                          });
                        }}
                        variant={"secondary"}
                      >
                        {t("core:view_profile")}
                      </Button>
                    }
                    className={"border-b-0"}
                  />
                </Stack>
              </Stack>
            </Stack>

            {!!changeGroups.length && (
              <Stack>
                <Title
                  header={t("approval:requested_changes")}
                  size={"xs"}
                  paddingBottom
                  paddingTop
                  badge={`(${changes.length})`}
                />
                <Stack gap={"sm"}>
                  {changeGroups.map((group) => (
                    <Stack
                      key={group.key}
                      className={"bg-dark/5 border border-dark/5 rounded-xs p-4"}
                    >
                      <Title header={group.title} size={"xs"} />
                      <Stack>
                        {group.changes.map((change, index) =>
                          change.field.type === ApiFieldTypes.employee_compensation_lines ? (
                            <Stack key={`${change.field.id}-${index}`} gap="sm">
                              <Text className={"pt-3 text-secondary"}>
                                {getFieldTitle(change.field)}
                              </Text>
                              <Stack direction="horizontal" gap="md">
                                {hasValueBefore(change) && (
                                  <Stack className="w-full" gap="xs">
                                    <Text className="text-secondary">
                                      {t("core:compensation_before_change")}
                                    </Text>
                                    <CompensationLinesValue
                                      lines={change.compensation_lines_before}
                                    />
                                  </Stack>
                                )}
                                <Stack className="w-full" gap="xs">
                                  {hasValueBefore(change) && (
                                    <Text className="text-secondary">
                                      {t("core:compensation_after_change")}
                                    </Text>
                                  )}
                                  <CompensationLinesValue lines={change.compensation_lines_after} />
                                </Stack>
                              </Stack>
                            </Stack>
                          ) : (
                            <ListItem
                              key={`${change.field.id}-${index}`}
                              preTitle={getFieldTitle(change.field)}
                              className={
                                index === group.changes.length - 1 ? "border-b-0" : undefined
                              }
                              titleSlot={
                                <Stack
                                  direction={"horizontal"}
                                  gap={"xs"}
                                  items={"center"}
                                  className={"min-w-0 font-semibold"}
                                >
                                  {hasValueBefore(change) && (
                                    <>
                                      <Text className={"min-w-0 break-words text-secondary"}>
                                        {renderChangedValue(change, change.value_before)}
                                      </Text>
                                      <Text className={"shrink-0 text-secondary"}>→</Text>
                                    </>
                                  )}
                                  <Text className={"min-w-0 break-words"}>
                                    {renderChangedValue(change, change.value_after)}
                                  </Text>
                                </Stack>
                              }
                            />
                          )
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
            <Title header={t("approval:request_details")} size={"xs"} paddingBottom paddingTop />
            <Stack>
              {approvalRequest.created_by && (
                <ListItem
                  className={"cursor-pointer"}
                  icon={<EmployeeAvatar employee={approvalRequest.created_by} />}
                  avatarUrl={approvalRequest.created_by.avatar_url ?? undefined}
                  preTitle={t("common:created_by")}
                  title={renderEmployeeName(approvalRequest.created_by)}
                  onClick={() => {
                    if (!approvalRequest.created_by) return;
                    openModal(EmployeeViewModal, {
                      employeeId: approvalRequest.created_by.id,
                    });
                  }}
                  valueSlot={
                    <Tooltip>
                      <TooltipTrigger>
                        {renderTimeAgo(approvalRequest.datetime_created_at)}
                      </TooltipTrigger>
                      <TooltipContent>
                        {renderDate(approvalRequest.datetime_created_at)}
                      </TooltipContent>
                    </Tooltip>
                  }
                />
              )}

              <ListItem
                emoji={":writing_hand:"}
                preTitle={t("common:workflow")}
                title={workflowData?.workflow.title}
                className={"border-b-0"}
                valueSlot={
                  isFeatureEnabled(ApiFeatureType.workflows) &&
                  canIEdit(ApiPermission.workflow_module) && (
                    <ButtonLink
                      target={"_blank"}
                      variant={"secondary"}
                      size={"sm"}
                      href={`/workflows/${workflowData?.workflow.id}/summary`}
                    >
                      {t("common:view")}
                    </ButtonLink>
                  )
                }
              />
            </Stack>

            <ApprovalsPersons
              request={approvalRequest}
              hideCreator
              showTextProgress
              title={t("approval:approvers")}
            />
          </Stack>
        )}
      </>
    </Modal>
  );
}
